import { HttpClient, HttpParams, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { TableSortConfig } from "@common/models/table-sort";
import {
    JobMaterial,
    Sample,
    SampleGroup,
    SampleGroupSourceMaterial,
    TaskJob,
    TaskInstance,
    Entity,
    TaskInput,
    TaskMaterial,
    cv_TaskStatus,
    Cohort
} from "@common/types";
import { DataManagerService } from "@services/data-manager.service";
import {EntityQuery, Predicate, QueryResult } from "breeze-client";
import { PartialJobMaterial, PartialMaterial } from "../models/partial-material";
import { MaterialType } from "../models/material-type";
import { LoggingService } from "@services/logging.service";
import { catchError, tap } from "rxjs/operators";
import { ToastrService } from "@services/toastr.service";
import { LogLevel } from "@services/models";
import { of } from "rxjs";
import { TaskStatusCount } from "../models/task-status-count";
import { batchArray } from "@common/util";

export interface UpdateAnimalSequenceRequest {
    JobKey: string,
    TableSorts: TableSortConfig[]
}

export interface BulkUpdateAnimalNameRequest {
    JobKey: number,
    Prefix: string,
    Suffix: string,
    Counter: number
}

export interface SampleGroupMetadata {
    C_SampleGroup_key?: number;
    SamplesCreatedCount?: number;
    NumberOfAnimalSourcesCount?: number;
    NumberOfSampleSourcesCount?: number;
    NumberOfAnimalPlaceholderSourcesCount?: number;
    HasEndStateMemberTask?: boolean;
}

export interface UpdateDueDateResponse {
    Success: string;
    ErrorMessage: string;
    AllAffectedTaskInstanceKeys: number[];
    AffectedGroupTaskInstanceKeys: number[];
}

export interface UpdateDueDateRequest {
    task: TaskInstance;
    date: Date
}

export interface MemberTaskInstanceForInputTabResponse {
    TaskInput: TaskInput[];
    TaskMaterial: TaskMaterial[];
    cv_TaskStatus: cv_TaskStatus;
}

const ERROR_UPDATING_TASK_DUE_DATES = "Error trying to update task due dates";

@Injectable()
export class JobPharmaDataAccessService {
    readonly LOG_TAG = 'job-pharma-data-access';
    constructor(
        private dataManagerService: DataManagerService,
        private httpClient: HttpClient,
        private loggingService: LoggingService,
        private toastrService: ToastrService
    ) { }

    // TODO: Replace Breeze query with GraphQL query
    async getSampleJobMaterials(jobKey: number, pageNumber: number): Promise<JobMaterial[]> {
        const predicates = [
            Predicate.create("Material.Sample.C_Material_key", "!=", null),
            Predicate.create("C_Job_key", "==", jobKey)
        ];
        const expands = [
            "Material.Sample",
            "Material.cv_MaterialType",
            "Material.MaterialSourceMaterial.SourceMaterial.Animal",
            "Material.MaterialSourceMaterial.SourceMaterial.Sample"
        ];
        const query = EntityQuery.from("JobMaterials")
            .expand(expands.join(", "))
            .where(Predicate.and(predicates))
            .skip(50 * (pageNumber - 1))
            .take(50);

        const result = await this.dataManagerService.executeQuery(query);

        return result.results as unknown as JobMaterial[];
    }

    // TODO: Replace Breeze query with GraphQL query
    async getSampleCount(jobKey: number): Promise<number>{
        const predicates = [
            Predicate.create("Material.Sample.C_Material_key", "!=", null),
            Predicate.create("C_Job_key", "==", jobKey)
        ];
        const expands = [
            "Material.Sample",
            "Material.cv_MaterialType",
        ];
        const query = EntityQuery.from("JobMaterials")
            .expand(expands.join(", "))
            .where(Predicate.and(predicates));

        return this.dataManagerService.returnQueryCount(query);
    }

    // TOOD: Pagination?
    async getSampleGroups(jobKey: number) {
        const predicates = [
            Predicate.create("C_Job_key", "==", jobKey),
            Predicate.create("TaskInstance.C_GroupTaskInstance_key", "==", null)
        ];
        const expands = [
            "TaskInstance.ProtocolInstance",
            "TaskInstance.SampleGroup"
        ];
        const orders = [
            "TaskInstance.DateDue",
            "Sequence",
            "TaskInstance.C_ProtocolInstance_key",
            "TaskInstance.ProtocolTask.SortOrder",
            "TaskInstance.C_WorkflowTask_key"
        ];

        const query = EntityQuery.from("TaskJobs")
            .expand(expands.join(","))
            .where(Predicate.and(predicates))
            .orderBy(orders.join(','));

        const { results } = await this.dataManagerService.executeQuery(query);
        const taskJobs = results as unknown as TaskJob[];
        return taskJobs.flatMap(tj => tj.TaskInstance.SampleGroup);
    }

    async getSampleGroupsMetadata(sampleGroupKeys: number[]) {
        if (!sampleGroupKeys || sampleGroupKeys.length === 0) {
            throw new Error('No sample group keys provided');
        }

        const params = { sampleGroupKeys: sampleGroupKeys.join(',') };
        const result = await this.httpClient.get('/api/samplegroup/metadata', { params }).toPromise() as SampleGroupMetadata[];

        return result;
    }

    async getSampleGroupSourceMaterials(sampleGroupKeys: number[]) {
        const predicates = [
            Predicate.create("C_SampleGroup_key", "in", sampleGroupKeys)
        ];
        const expands = [
            "Material.Sample",
            "Material.Animal",
            "AnimalPlaceholder.Material.Animal"
        ];

        const query = EntityQuery.from("SampleGroupSourceMaterials")
            .expand(expands.join(','))
            .where(Predicate.and(predicates))

        const { results } = await this.dataManagerService.executeQuery(query);
        return results as unknown as SampleGroupSourceMaterial[];
    }

    async getSamplesCreatedFromSampleGroups(sampleGroupKeys: number[]) {
        const predicates = [
            Predicate.create("C_SampleGroup_key", "in", sampleGroupKeys)
        ];
        const expands = [
            "Material.MaterialSourceMaterial.SourceMaterial",
            "Material.MaterialSourceMaterial.SourceMaterial.Animal",
            "Material.MaterialSourceMaterial.SourceMaterial.Sample",
        ];

        const query = EntityQuery.from("Samples")
            .expand(expands.join(','))
            .where(Predicate.and(predicates))

        const { results } = await this.dataManagerService.executeQuery(query);
        return results as unknown as Sample[];
    }

    async loadSampleGroupSourceMaterials(sampleGroupKeys: number[]): Promise<void>{
        if (sampleGroupKeys.length == 0) {
            return;
        }

        await this.dataManagerService.executeQuery(new EntityQuery("SampleGroupSourceMaterials")
            .where("C_SampleGroup_key", "in", sampleGroupKeys));
    }

    removeSampleGroupSourceMaterials(sampleGroupSourceMaterials: SampleGroupSourceMaterial[]) {
        for (const sgsm of sampleGroupSourceMaterials) {
            this.dataManagerService.deleteEntity(sgsm);
        }
    }

    removeSampleGroup(sampleGroups: SampleGroup[]) {
        for (const sg of sampleGroups) {
            // prevent breeze from updating sample group source material
            const sampleGroupSourceMaterialsToDetach = [...sg.SampleGroupSourceMaterial];
            for (const sgsm of sampleGroupSourceMaterialsToDetach) {
                this.dataManagerService.detachEntity(sgsm);
            }
            this.dataManagerService.deleteEntity(sg);
        }
    }

    async getAllMaterialsOnJob(jobKey: number, materialType: MaterialType, ignoredJobMaterialKeys?: number[]): Promise<(PartialMaterial & PartialJobMaterial)[]> {
        const url = `/api/jobdata/${jobKey}/${materialType}`;
        let params = new HttpParams();
        if (ignoredJobMaterialKeys?.length > 0) {
            params = params.set("ignoredMaterialKeys", ignoredJobMaterialKeys.join(','));
        }

        const result = await this.httpClient.get(url, { params }).toPromise() as (PartialMaterial & PartialJobMaterial)[];

        return result;
    }

    async updateTaskDateDues(updates: UpdateDueDateRequest[], source = this.LOG_TAG): Promise<UpdateDueDateResponse> {
        const taskInstanceKeysToUpdate = new Set(updates.map(u => u.task.C_TaskInstance_key));
        return this.httpClient.post<UpdateDueDateResponse>('/api/jobdata/updateTaskDateDue', {
            TaskDateDues: updates.map(u => ({ TaskInstanceKey: u.task.C_TaskInstance_key, DateDue: u.date })),
            source
        }).pipe(
            tap((response) =>{
                this.loggingService.logFacetSaveSuccess(this.LOG_TAG, true);

                if (response.AffectedGroupTaskInstanceKeys.some(key => !taskInstanceKeysToUpdate.has(key))) {
                    this.toastrService.showToast(
                        'Dependent due dates and start times have been updated. ' +
                        'Please check the schedule.',
                        LogLevel.Warning
                    )
                }
            }
            ),
            catchError((err) => {
                this.loggingService.logError(
                    ERROR_UPDATING_TASK_DUE_DATES,
                    err,
                    this.LOG_TAG,
                    false
                )
                this.toastrService.showToast(ERROR_UPDATING_TASK_DUE_DATES, LogLevel.Error);
                return of();
            })
        ).toPromise() as Promise<UpdateDueDateResponse>;
    }

    async getAnimalJobMaterials(jobKey: number, pageNumber: number, orderBy = "Sequence", additionalExpands: string[] = []): Promise<JobMaterial[]> {
        const predicates = [
            Predicate.create("Material.Animal.C_Material_key", "!=", null),
            Predicate.create("C_Job_key", "==", jobKey)
        ];

        const expands = [
            'Material.MaterialPoolMaterial.MaterialPool',
            'Material.CohortMaterial.Cohort',
            'Material.Animal.cv_Sex',
            'Material.Animal.cv_AnimalStatus',
            'Material.Animal.Genotype',
            'Material.Line',
            ...additionalExpands
        ];

        const query = EntityQuery.from("JobMaterials")
            .expand(expands.join(", "))
            .where(Predicate.and(predicates))
            .orderBy(orderBy)
            .skip(50 * (pageNumber - 1))
            .take(50);

        const result = await this.dataManagerService.executeQuery(query);
        return result.results as unknown as JobMaterial[];
    }

    getAnimalCount(jobKey: number): Promise<number> {
        const predicates = [
            Predicate.create("Material.Animal.C_Material_key", "!=", null),
            Predicate.create("C_Job_key", "==", jobKey)
        ];
        const query = EntityQuery.from("JobMaterials")
            .where(Predicate.and(predicates));

        return this.dataManagerService.returnQueryCount(query);
    }

    checkAnimalSequence(data: UpdateAnimalSequenceRequest): Promise<boolean> {
        return this.httpClient.post(
            "/api/jobdata/checkAnimalSequence",
            data,
            { headers: new HttpHeaders() }
        ).toPromise() as Promise<boolean>;
    }

    updateAnimalSequence(data: UpdateAnimalSequenceRequest): Promise<void> {
        return this.httpClient.post(
            "/api/jobdata/updateAnimalSequence",
            data,
            { headers: new HttpHeaders() },
        ).toPromise() as unknown as Promise<void>;
    }

    bulkUpdateAnimalName(data: BulkUpdateAnimalNameRequest): Promise<void>{
        return this.httpClient.post(
            "/api/jobdata/bulkUpdateAnimalName",
            data,
            { headers: new HttpHeaders() }
        ).toPromise() as unknown as Promise<void>;
    }

    getTaskStatusCounts(jobKey: number): Promise<Record<number, TaskStatusCount>> {
        return this.httpClient.get(
            `/api/jobdata/taskStatusCounts/${jobKey}`
        ).toPromise() as unknown as Promise<Record<number, TaskStatusCount>>;
    }

    async getProtocolStatusCounts(jobKey: number) {
        const url = `/api/jobdata/protocolStatusCounts/${jobKey}`;
        const result = await this.httpClient.get(url).toPromise();
        return result;
    }

    async getGroupTaskInstances(jobKey: number): Promise<TaskInstance[]> {
        const predicates = [
            Predicate.create("TaskJob", "any", "C_Job_key", "eq", jobKey),
            Predicate.create("C_GroupTaskInstance_key", "==", null),
        ];

        const expands = [
            "LocationPosition",
            "ProtocolInstance.Protocol",
            "ProtocolTask.cv_ScheduleType",
            "WorkflowTask",
        ];

        const query = new EntityQuery("TaskInstances")
            .expand(expands.join(','))
            .where(Predicate.and(predicates));

        const { results } = await this.dataManagerService.executeQuery(query);
        return results as unknown as TaskInstance[]
    }

    /**
     * Gets a sorted list of group task instance keys. Once sorted, the keys are returned as pages of 50.
     * @param jobKey
     * @param page starts at page 1
     * @returns
     */
    async getPagedAndSortedGroupTaskInstanceKeys(jobKey: number, page: number, pageSize: number): Promise<number[]> {
        if (jobKey < 0) {
            return [];
        }
        const url = `/api/jobdata/getSortedGroupTaskInstanceKeys`
        let params = new HttpParams();
        params = params.append("jobKey", jobKey.toString());
        params = params.append("page", page.toString());
        params = params.append("pageSize", pageSize.toString());

        return (await this.httpClient.get<number[]>(url, { params }).toPromise())

    }

    /**
     * Gets the TaskJobs associated with group task instances.
     * Expands the TaskInstance to load necessary data.
     * @param groupTaskInstanceKeys
     * @returns
     */
    public async getGroupTaskJobs(groupTaskInstanceKeys: number[]): Promise<Entity<TaskJob>[]>{
        if (groupTaskInstanceKeys.length == 0) {
            return [];
        }

        const query = new EntityQuery("TaskJobs")
            .expand("TaskInstance.TaskCohort")

        return await this.dataManagerService.fetchByBatchKeys(groupTaskInstanceKeys, query, "C_TaskInstance_key") as Entity<TaskJob>[];
    }

    /**
     * Gets the total amount of group task instances on a job, for pagination.
     * @param jobKey
     * @param excludeNonMaterial if true, adds predicates that only look for jobs where NoMaterials is either false or null.
     * @returns
     */
    async getGroupTaskInstancesCount(jobKey:number): Promise<number> {
        if (jobKey < 0) {
            return 0;
        }
        const predicates = [
            Predicate.create("C_GroupTaskInstance_key", "eq", null),
            Predicate.create("TaskJob", "any", "C_Job_key", "eq", jobKey)
        ];

        return await this.dataManagerService.returnQueryCount(new EntityQuery("TaskInstances")
            .where(Predicate.and(predicates)));
    }

    /**
     * Gets the amount of task instances on a protocol instance. Excludes non-material tasks.
     * @param protocolInstanceKey
     * @returns
     */
    async getGroupTasksOnProtocolCount(protocolInstanceKey: number): Promise<number> {
        if (protocolInstanceKey < 0) {
            return 0;
        }

        const isPartOfProtocolInstance = Predicate.create("C_ProtocolInstance_key", "eq", protocolInstanceKey);
        const nonMaterialIsFalsy = Predicate.or([
            Predicate.create("WorkflowTask.NoMaterials", "eq", false),
            Predicate.create("WorkflowTask.NoMaterials", "eq", null)
        ]);
        return await this.dataManagerService.returnQueryCount(new EntityQuery("TaskInstances")
            .where(isPartOfProtocolInstance)
            .where(nonMaterialIsFalsy));
    }

    /**
     * Loads the task instances on a protocol instance. Excludes non-material tasks.
     * @param protocolInstanceKey
     * @returns
     */
    async loadTaskInstancesWithNoMaterialsOnProtocol(protocolInstanceKey: number): Promise<TaskInstance[]> {
        if (protocolInstanceKey < 0) {
            return [];
        }

        const isPartOfProtocolInstance = Predicate.create("C_ProtocolInstance_key", "eq", protocolInstanceKey);
        const nonMaterialIsFalsy = Predicate.or([
            Predicate.create("WorkflowTask.NoMaterials", "eq", false),
            Predicate.create("WorkflowTask.NoMaterials", "eq", null)
        ]);

        const { results } = await this.dataManagerService.executeQuery(new EntityQuery("TaskInstances")
            .where(isPartOfProtocolInstance)
            .where(nonMaterialIsFalsy));

        return results as unknown as TaskInstance[];
    }

    async getTaskInstancesByKeys(keys: number[]): Promise<TaskInstance[]> {
        const promises: Promise<TaskInstance[]>[] = [];
        for (const batch of batchArray(keys, 100)) {
            promises.push((async () => {
                const predicates = [
                    Predicate.create("C_TaskInstance_key", "in", batch),
                ];

                const query = new EntityQuery("TaskInstances")
                    .where(Predicate.and(predicates));

                const { results } = await this.dataManagerService.executeQuery(query);
                return results as unknown as TaskInstance[];
            })());
        }

        return (await Promise.all(promises)).flatMap(x => x);
    }

    loadMemberTaskInstances(groupKey: number): Promise<QueryResult> {
        const predicate = Predicate.create("C_GroupTaskInstance_key", "==", groupKey);
        const query = new EntityQuery('TaskInstances').where(predicate);

        return this.dataManagerService.executeQuery(query);
    }

    addCohortsToTask(
        jobKey: number,
        taskInstanceKey: number,
        cohortKeys: number[],
    ): Promise<void> {
        const httpOptions = {
            headers: new HttpHeaders({
                'x-single-request': '1', // disable multiple requests when network errors
            }),
        };

        return this.httpClient.post('/api/jobtaskinstances/add-cohorts-to-task', {
            JobKey: jobKey,
            TaskInstanceKey: taskInstanceKey,
            CohortKeys: cohortKeys,
        }, httpOptions).toPromise()  as unknown as Promise<void>;
    }

    async getIncompleteTaskCount(jobKey: number) {
        const areIncompleteMemberTasksOnJob = Predicate.and([
            Predicate.create("TaskJob", "any", "C_Job_key", "==", jobKey),
            Predicate.create("C_GroupTaskInstance_key", "!=", null),
            Predicate.create("cv_TaskStatus.IsEndState", "==", false)
        ]);

        const incompleteTasksQuery = new EntityQuery("TaskInstances")
        .where(areIncompleteMemberTasksOnJob);

        return this.dataManagerService.returnQueryCount(incompleteTasksQuery);
    }
    
    async loadTaskInstancesFromProtocolInstance(taskInstances: TaskInstance[]): Promise<void> {
        const expands = [
            "SampleGroup.SampleGroupSourceMaterial",
            "SampleGroup.Sample",
            "TaskInput",
            "ProtocolTask.InputDefault",
            "TaskPlaceholder",
        ];

        await this.dataManagerService.ensureRelationships(taskInstances, expands);
    }

    async loadLocalTaskJobs(jobKey: number): Promise<Entity<TaskJob>[]> {
        return (await this.dataManagerService.executeQueryLocal(new EntityQuery("TaskJobs")
            .where("C_Job_key", "eq", jobKey))).results as Entity<TaskJob>[];
    }

    async getGroupTaskInstancesForInputsTab(jobKey: number): Promise<TaskInstance[]> {
        const predicates = [
            Predicate.create("TaskJob", "any", "C_Job_key", "eq", jobKey),
            Predicate.create("C_GroupTaskInstance_key", "==", null),
        ];

        const expands = [
            'TaskMaterial.Material.Animal',
            'TaskMaterial.Material.Sample',
            'ProtocolInstance',
            'TaskPlaceholder.TaskPlaceholderInput.Input.cv_DataType',
            'TaskPlaceholder.Placeholder.JobCohort',
            'WorkflowTask',

            'TaskInput.Input.cv_DataType',
            'TaskInput.Input.cv_DosingTable',
            'TaskInput.Input.cv_JobCharacteristicType'
        ];

        const query = new EntityQuery("TaskInstances")
            .expand(expands.join(','))
            .where(Predicate.and(predicates));

        const { results } = await this.dataManagerService.executeQuery(query);
        return results as unknown as TaskInstance[]
    }

    async getCohortsForInputsTab(key: number): Promise<Cohort[]> {
        const predicates = [
            Predicate.create("C_Job_key", "eq", key),
        ];

        const expands = [
            'Cohort.TaskCohort.TaskCohortInput.Input.cv_DataType',
            'Cohort.CohortMaterial.Material.Animal',
        ];

        const query = new EntityQuery("JobCohorts")
            .select("Cohort")
            .expand(expands.join(','))
            .where(Predicate.and(predicates));

        const { results } = await this.dataManagerService.executeQuery(query);
        return results as unknown as Cohort[]
    }

    async getMemberTaskInstancesWitExpandsForInputsTab(keys: number[]): Promise<MemberTaskInstanceForInputTabResponse[]> {
        const promises: Promise<TaskInstance[]>[] = [];

        for (const batch of batchArray(keys, 5)) {
            promises.push((async () => {
                const predicates = [
                    Predicate.create("C_GroupTaskInstance_key", "!=", null),
                    Predicate.create("C_GroupTaskInstance_key", "in", batch),
                ];

                const expands = [
                    'TaskMaterial',
                    'TaskInput.Input.cv_DataType',
                    'cv_TaskStatus',
                ];

                const query = new EntityQuery("TaskInstances")
                    .select("TaskMaterial, TaskInput, cv_TaskStatus")
                    .expand(expands.join(','))
                    .where(Predicate.and(predicates));

                const { results } = await this.dataManagerService.executeQuery(query);
                return results as unknown as TaskInstance[]
            })());
        }

        return (await Promise.all(promises)).flatMap(x => x);
    }
}
